/**
 * MaterieelTypeSelect — a search-as-you-type multi-select for materieeltypen.
 *
 * Unlike CreatableSelectField / MaterieelTypenSearchField, this component:
 * - Is NOT coupled to react-hook-form — accepts plain value/onChange (string[]).
 * - Opens the dropdown on click or while typing.
 * - Intercepts Enter to create free-text values when the typed input doesn't
 *   exactly match a known option (e.g. typing "SNG" won't auto-select "SNG-3").
 * - Uses startsWith filtering instead of contains-anywhere.
 * - Accepts grouped options (from useMaterieelTypenSearch).
 *
 * Use MaterieelTypenSearchField when working inside a react-hook-form with
 * the standard filter-dialog pattern. Use this component when you need direct
 * value/onChange control and the search-then-create UX.
 */
import { FC, useState } from 'react';
import CreatableSelect from 'react-select/creatable';

import { CustomMultiValueRemove } from './CreatableChipInput';

type EenhedenSelectProps = {
  /** Grouped options from useMaterieelTypenSearch */
  options: { value: string; label: string }[];
  selectedEenheden: string[];
  createOption: (label: string) => { label: string; value: string };
  /** Called with the updated string array when selection changes */
  onChange: (values: string[]) => void;
  inputId?: string;
  placeholder?: string;
};

const EenhedenSelect: FC<EenhedenSelectProps> = ({
  options,
  selectedEenheden,
  createOption,
  onChange,
  inputId = 'eenhedenSelect',
  placeholder = 'Voeg nummer(s) toe',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
    }
    if (e.key === ',' || e.key === ' ' || e.key === 'Enter') {
      if (!inputValue.trim()) return;
      e.preventDefault();
      const newValues = [...selectedEenheden, inputValue.trim().toUpperCase()];
      onChange(newValues);
      setInputValue('');
    }
  };

  return (
    <CreatableSelect
      inputId={inputId}
      id={`create-select-${inputId}`}
      isMulti
      isClearable
      isSearchable
      maxMenuHeight={200}
      placeholder={placeholder}
      className='react-select-container'
      classNamePrefix='react-select'
      noOptionsMessage={() => 'Geen opties'}
      formatCreateLabel={(input) => `Eenheidnummer: ${input}`}
      options={options}
      value={selectedEenheden.map((item) => createOption(item))}
      inputValue={inputValue}
      menuIsOpen={menuIsOpen}
      onMenuOpen={() => setMenuIsOpen(true)}
      onMenuClose={() => setMenuIsOpen(false)}
      onInputChange={(val, action) => {
        if (action.action === 'input-blur' || action.action === 'menu-close') {
          setInputValue('');
        } else {
          setInputValue(val);
        }
      }}
      onKeyDown={handleKeyDown}
      onChange={(newVal) => {
        const values = newVal ? newVal.map((v: any) => v.value) : [];
        onChange(values);
        setInputValue('');
      }}
      components={{ MultiValueRemove: CustomMultiValueRemove }}
    />
  );
};
export default EenhedenSelect;
